import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { GriboullisIcon } from "@images/svgIcons/griboullisIcon"
import { BoxInfoResume } from "@modules/box/components/boxInfoResume"
import { useBoxStaffInvitationSidebar } from "@modules/boxStaff/hooks/useBoxStaffInvitationSidebar"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { NewsResume } from "@modules/dashboard/components/newsResume"
import { ObjectivesResume } from "@modules/dashboard/components/objectivesResume"
import { ProfileResume } from "@modules/dashboard/components/profileResume"
import { RightNowResume } from "@modules/dashboard/components/rightNowResume"
import { SessionsResume } from "@modules/dashboard/components/sessionsResume"
import { StripeAccountErrorAlert } from "@modules/finances/components/stripeAccountErrorAlert"
import { useHome } from "@modules/home/hooks/useHome"
import { useMemberSideBar } from "@modules/members/hooks/useMemberSidebar"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { useInvitationSidebar } from "@modules/sharedCoach/hooks/useInvitationSidebar"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { useEffect, Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { useUser } from "@/modules/users/hooks/useUser"

export function DashboardPage({ className, ...props }: { className?: string } & React.ComponentProps<"div">) {
  const user = useUser()
  const { navigateToInvitationDetailsInfo } = useInvitationSidebar()
  const { navigateToBoxStaffInvitation } = useBoxStaffInvitationSidebar()
  const { navigateToProgramBundleResume } = useProgramBundleSidebar()
  const { navigateToMemberOfferInvitation } = useMemberSideBar()
  const { sidebarNavigateTo } = useSidebar()
  const { homeContent } = useHome()
  const { localStorageService } = useServicesContext()

  const programId = localStorage.getItem(LocalStorageKeys.sharedCoachInvitation.programId)
  const boxId = localStorage.getItem(LocalStorageKeys.boxStaffInvitation.boxId)
  const programBundleInvitation = localStorageService.load(LocalStorageKeys.programBundleInvitation, {
    programBundleId: "",
  })
  const boxInvitation = localStorageService.load(LocalStorageKeys.boxInvitation, {
    boxId: "",
  })
  const boxOfferInvitation = localStorageService.load(LocalStorageKeys.boxOfferInvitation.invitation, {
    boxId: "",
    boxOfferId: "",
    shortLink: "",
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      if (boxId && boxId !== "") {
        navigateToBoxStaffInvitation({
          onHide: () => {
            localStorage.removeItem(LocalStorageKeys.boxStaffInvitation.boxId)
            return Promise.resolve(true)
          },
          replaceAllComponents: true,
        })
      }

      if (programId && programId !== "") {
        navigateToInvitationDetailsInfo({
          onHide: () => {
            localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
            return Promise.resolve(true)
          },
          replaceAllComponents: true,
        })
      }

      if (programBundleInvitation && programBundleInvitation.programBundleId !== "") {
        navigateToProgramBundleResume(programBundleInvitation.programBundleId, {
          replaceAllComponents: true,
        })

        localStorage.removeItem(LocalStorageKeys.programBundleInvitation)
      }

      if (boxInvitation && boxInvitation.boxId !== "") {
        void sidebarNavigateTo({
          content: (
            <ErrorBoundary fallback={<ErrorView />}>
              <Suspense fallback={<LoadingSpinner />}>
                <BoxInfoResume boxId={boxInvitation.boxId} />
              </Suspense>
            </ErrorBoundary>
          ),
          replaceAllComponents: true,
        })

        localStorage.removeItem(LocalStorageKeys.boxInvitation)
      }

      if (
        boxOfferInvitation &&
        boxOfferInvitation.boxOfferId !== "" &&
        boxOfferInvitation.shortLink !== "" &&
        boxOfferInvitation.boxId !== ""
      ) {
        localStorage.removeItem(LocalStorageKeys.boxOfferInvitation.invitation)

        navigateToMemberOfferInvitation(boxOfferInvitation, {
          replaceAllComponents: true,
        })
      }
    }, 2000)

    return () => {
      clearInterval(timer)
    }
  }, [programId, boxId])

  return (
    <>
      <div
        className={cn(
          "flex h-full w-full flex-row flex-wrap justify-between p-6 pt-2 pb-3 min-[1480px]:h-svh min-[1960px]:mx-auto min-[1960px]:w-376",
          className,
        )}
        {...props}
      >
        {/* Left column */}
        <div className="flex w-full flex-col justify-between gap-4 min-[1180px]:max-[1480px]:w-[49%] min-[1480px]:w-[32%]">
          <ProfileResume user={user} />
          <div className="mb-25px relative mt-3 flex flex-col">
            <Text fontStyle="BS">{word("home.screen.title", { firstname: user.firstname })}</Text>
            <Text fontStyle="HS">{word("home.screen.subtitle")}</Text>
            <GriboullisIcon className="absolute bottom-[20px] left-[205px]" color="var(--primary-500)" />
          </div>
          <SessionsResume />
        </div>

        {/* Center column */}
        <div className="mt-8 flex w-full flex-col gap-4 min-[1180px]:mt-0 min-[1180px]:max-[1480px]:w-[49%] min-[1480px]:w-[36%]">
          {homeContent?.shouldDisplayRightNowSection() && <RightNowResume />}
          <ObjectivesResume />
        </div>

        {/* Right column */}
        <div className="mt-8 flex w-full min-w-80 flex-col content-center min-[1480px]:mt-0 min-[1480px]:h-full min-[1480px]:w-[28%]">
          <StripeAccountErrorAlert orientation="vertical" />
          <NewsResume />
        </div>
      </div>
    </>
  )
}
